import React, { useState, useEffect } from 'react';
import Flashbar, { FlashbarProps } from '@amzn/awsui-components-react/polaris/flashbar';
import Button from '@amzn/awsui-components-react/polaris/button';
import { getStageByOrigin } from './requestConfig';

export interface IsItDownBannerItem {
  id: string;
  type: FlashbarProps.Type;
  header?: string;
  content: string;
  dismissible?: boolean;
  action?: React.ReactNode;
}

const getIsItDownConfig = (stage: string | null) => {
  switch (stage) {
    case 'dev':
    case 'alpha':
    case 'alpha-itl':
    case 'local':
      return {
        componentIds: ['22498', '22489'],
        baseUrl: 'https://is-it-down.amazon.com/Volunteer_Center_Alpha',
      };
    case 'beta':
      return {
        componentIds: ['22499', '22500'],
        baseUrl: 'https://is-it-down.amazon.com/Volunteer_Center_Beta',
      };
    case 'prod':
      return {
        componentIds: ['22501', '22503'],
        baseUrl: 'https://is-it-down.amazon.com/Volunteer_Center',
      };
    default:
      return null;
  }
};

const loadIsItDownScript = (componentId: string, baseUrl: string): Promise<void> => {
  return new Promise((resolve) => {
    const scriptId = `isitdown-stripe-script-${componentId}`;

    // Don't load if already exists
    if (document.getElementById(scriptId)) {
      resolve();
      return;
    }

    const script = document.createElement('script');
    script.id = scriptId;
    script.src = `${baseUrl}/components/${componentId}/stripe.js`;
    script.async = true;

    script.onload = () => resolve();
    script.onerror = () => resolve(); // Don't fail if script doesn't load

    document.head.appendChild(script);
  });
};

const processIsItDownElement = (
  componentId: string,
  element: HTMLElement,
  baseUrl: string
): IsItDownBannerItem | null => {
  // Get button text from the link element
  const linkElement = element.querySelector('.is-it-down-link') as HTMLElement;
  const buttonText = linkElement?.textContent?.trim() || 'Is Volunteer Center Down?';

  // Get content by cloning element and removing the link
  const contentElement = element.cloneNode(true) as HTMLElement;
  const linkToRemove = contentElement.querySelector('.is-it-down-link');
  if (linkToRemove) {
    linkToRemove.remove();
  }
  const content = contentElement.textContent?.trim() || '';

  // Hide original banner
  element.style.display = 'none';

  // Only show if there's meaningful content
  if (
    !content ||
    content.toLowerCase().includes('operational') ||
    content.toLowerCase().includes('no data')
  ) {
    return null;
  }

  // Map IsItDown levels to Cloudscape Flashbar types
  let flashbarType: FlashbarProps.Type = 'warning'; // default
  if (element.classList.contains('level-one')) {
    flashbarType = 'info';
  } else if (element.classList.contains('level-two')) {
    flashbarType = 'warning';
  } else if (element.classList.contains('level-three')) {
    flashbarType = 'error';
  }

  return {
    id: `isitdown-${componentId}`,
    type: flashbarType,
    content: content,
    dismissible: true,
    action: (
      <Button
        variant="link"
        href={`${baseUrl}/components/${componentId}`}
        target="_blank"
        iconName="external"
      >
        {buttonText}
      </Button>
    ),
  };
};

export const useIsItDownBanners = (): IsItDownBannerItem[] => {
  const [banners, setBanners] = useState<IsItDownBannerItem[]>([]);

  useEffect(() => {
    const stage = getStageByOrigin();

    const config = getIsItDownConfig(stage);

    if (!config) return;

    const fetchAllStatuses = async () => {
      try {
        // Load all scripts in parallel
        await Promise.all(config.componentIds.map((id) => loadIsItDownScript(id, config.baseUrl)));

        // Poll for banners (like original code)
        const activeBanners: IsItDownBannerItem[] = [];
        const processedComponents = new Set<string>();

        for (let attempt = 0; attempt < 10; attempt++) {
          // Check all components
          for (const componentId of config.componentIds) {
            const bannerId = `${componentId}-stripe`;
            const element = document.getElementById(bannerId);

            if (element && element.textContent?.trim() && !processedComponents.has(componentId)) {
              processedComponents.add(componentId);
              const bannerItem = processIsItDownElement(componentId, element, config.baseUrl);
              if (bannerItem) {
                activeBanners.push(bannerItem);
              }
            }
          }

          // Update state after each attempt
          setBanners([...activeBanners]);

          // Check if we've processed all components (not necessarily created banners for all)
          if (processedComponents.size === config.componentIds.length) {
            break;
          }

          // Wait before next attempt (except on last)
          if (attempt < 9) {
            await new Promise((resolve) => setTimeout(resolve, 1000));
          }
        }
      } catch (error) {
        console.error('IsItDown: Error fetching statuses:', error);
        setBanners([]);
      }
    };

    // Initial fetch
    fetchAllStatuses();

    // Poll for updates every 2 minutes (scripts need time to load)
    const interval = setInterval(fetchAllStatuses, 2 * 60 * 1000);

    return () => clearInterval(interval);
  }, []);

  return banners;
};

export function IsItDownFlashbar() {
  const banners = useIsItDownBanners();
  const [dismissedBanners, setDismissedBanners] = useState<Set<string>>(new Set());

  const visibleBanners = banners.filter((banner) => !dismissedBanners.has(banner.id));

  const handleDismiss = (bannerId: string) => {
    setDismissedBanners((prev) => new Set(Array.from(prev).concat(bannerId)));
  };

  if (visibleBanners.length === 0) return null;

  const flashbarItems: FlashbarProps.MessageDefinition[] = visibleBanners.map((banner) => ({
    type: banner.type,
    header: banner.header,
    content: banner.content,
    dismissible: banner.dismissible,
    onDismiss: banner.dismissible ? () => handleDismiss(banner.id) : undefined,
    action: banner.action,
    id: banner.id,
  }));

  return (
    <div className="isitdown-flashbar-wrapper">
      <Flashbar items={flashbarItems} />
    </div>
  );
}
